import cx from 'classnames';
import {
  ALLOWED_FILTER_ASSETS_BY_LIST,
  EnrichedAccountDetailAsset,
  FormHeader,
  TradeFormValues,
  useCurrencyInfoContext,
  useWorkflowBuySellList,
  BigOrZero,
  FILTER_ASSETS_BY,
  AppIcon,
} from 'common';
import React, {
  FC,
  PropsWithChildren,
  memo,
  useCallback,
  useState,
} from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import { AppStore, DataStore } from '~/store';
import { TRADE_STEPS } from './steps';
import { AppLoader } from '~/components/app-loader';
import { API } from '@xbto/api-client';
import { NotAllowed } from '../shared/not-allowed';
import { getAccountType } from '~/utils/get-account-details';
import {
  AmountChangePercent,
  AmountPrice,
  Button,
  CurrencyIdentity,
  Icon,
  Row,
  SearchEmpty,
  SearchInput,
  SortableHeaderTitle,
  TabItemSwitcher,
  TextBody,
} from '@xbto/universal-components';
import startCase from 'lodash/startCase';

interface AssetItemProps {
  asset: EnrichedAccountDetailAsset;
  disabled: boolean;
  showPercentageChange?: boolean;
  onSelected: (asset: EnrichedAccountDetailAsset) => void;
}

const AssetItem: FC<AssetItemProps> = ({
  asset,
  onSelected,
  disabled,
  showPercentageChange = false,
}) => {
  /**
   * Methods
   */
  const handleOnSelected = useCallback(() => {
    if (disabled) {
      return;
    }
    onSelected(asset);
  }, [asset, onSelected, disabled]);

  /**
   * DOM
   */
  return (
    <div
      className={cx(
        'flex flex-row w-full justify-between items-center px-2 border-b hover-bg-grey-brighter',
        {
          'cursor-pointer': !disabled,
          'bg-grey-brighter cursor-not-allowed opacity-50': disabled,
        }
      )}
      style={{
        ...(disabled && {
          filter: 'grayscale(100%)',
        }),
      }}
      onClick={handleOnSelected}
    >
      <p className="flex flex-1 gap-x-2.5 px-4 py-3 items-center justify-between">
        <CurrencyIdentity
          code={asset?.currency.code}
          displayCode={asset?.currency.displayCode}
          name={asset?.currency.name}
        />

        <span className="flex flex-col items-end">
          <TextBody em>
            {showPercentageChange
              ? asset.currency.fxRate.formatted.rateWithCurrencyCode
              : asset.formatted.value}
          </TextBody>
          {showPercentageChange ? (
            <AmountChangePercent
              value={BigOrZero(
                asset.currency.fxRate.rate24HoursAgoPercentageChange
              )}
            />
          ) : (
            <TextBody small>{asset.formatted.balanceWithCurrencyCode}</TextBody>
          )}
        </span>
      </p>
    </div>
  );
};

interface AssetAllowedProps extends PropsWithChildren {
  busy: boolean;
  allowed: boolean;
  formValues: TradeFormValues;
  assetCode: string;
}

const AssetAllowed: FC<AssetAllowedProps> = ({
  busy,
  allowed,
  assetCode,
  formValues,
  children,
}) => {
  /**
   * Store
   */
  const setSelectedDialogType = AppStore.useStoreActions(
    a => a.setDashboardSelectedDialogType
  );
  const accountDetail = DataStore.useStoreState(s => s.portfolio.accountDetail);

  /**
   * DOM
   */
  if (busy) {
    return <AppLoader bgColor={'bg-transparent'} spinnerTop="50%" />;
  }
  if (allowed) {
    return <>{children}</>;
  }
  return (
    <NotAllowed
      message={`You cannot ${formValues.side.toLocaleLowerCase()} ${assetCode} in your account ${getAccountType(
        accountDetail?.account
      )} ${accountDetail?.account?.accountNumber}`}
      onClose={() => setSelectedDialogType(null)}
    />
  );
};

export const TradeList: FC<Partial<StepWizardChildProps>> = memo(
  ({ goToNamedStep }) => {
    if (!goToNamedStep) {
      console.error(
        `Component: AssetSelector, can only be used as a child of StepWizard.`
      );
      return null;
    }

    /**
     * Store
     */
    const selectedCurrencyCode = AppStore.useStoreState(
      s => s.selectedCurrencyCode
    );
    const setSelectedDialogType = AppStore.useStoreActions(
      a => a.setDashboardSelectedDialogType
    );

    /**
     * Vars
     */
    const selectedCurrency = useCurrencyInfoContext(selectedCurrencyCode);
    const [searchCollapsed, setSearchCollapsed] = useState(true);

    /**
     * Methods
     */
    const onBackHandler = useCallback(() => {
      goToNamedStep(TRADE_STEPS.FORM);
    }, [goToNamedStep]);
    // Note: do not memoize / useCallback this handler
    const onAssetSelected = (asset: EnrichedAccountDetailAsset) => {
      setAssetSearch('');
      setFormValues({
        ...formValues,
        ...(formValues.fromAsset ? { toAsset: asset } : { fromAsset: asset }),
        ...(!formValues.amountSide && !formValues.fromAsset
          ? {
              amountSide: 'fromAssetAmount',
            }
          : undefined),
      });

      goToNamedStep(TRADE_STEPS.FORM);
    };

    /**
     * Hooks
     */
    const {
      error,
      formValues,
      assetFilter,
      assetSearch,
      allowed,
      groupedAssets,
      hasNoAssets,
      assetListTitle,
      busy,
      assetListHeader,
      setAssetSearch,
      setAssetFilter,
      setFormValues,
    } = useWorkflowBuySellList({
      DataStore,
      ccyCode: selectedCurrency?.code,
      onSelected: onAssetSelected,
    });
    const handleSearch = useCallback(
      (value: string) => setAssetSearch?.(value || ''),
      [setAssetSearch]
    );
    const handleAssetFilterChange = useCallback(
      (id: FILTER_ASSETS_BY) => () => setAssetFilter(id),
      [setAssetFilter]
    );
    const toggleSearchCollapsed = useCallback(() => {
      setSearchCollapsed(c => !c);
    }, [setSearchCollapsed]);

    /**
     * DOM
     */
    return (
      <div className="flex flex-col max-w-600 min-h-680">
        {/* header  */}
        {/* // TODO(Hadrien): Refactor TradeList / transaction-workflows AppDialog into new Modal UC.
          Keep the double row for now, we want exactly 72px height for the header, this will be removed when migrating to the new Modal UC.
         */}
        <Row.C p="6" style={{ position: 'relative' }}>
          {formValues?.fromAsset && (
            <div
              onClick={onBackHandler}
              className="absolute left-6 cursor-pointer"
            >
              <Icon name="ArrowBack" />
            </div>
          )}
          {/* // TODO(Hadrien): Remove xem to em when migrating to the new Modal UC. */}
          <TextBody xem>{assetListTitle}</TextBody>
        </Row.C>
        <>
          {!!error ? (
            <NotAllowed
              message={error}
              onClose={() => setSelectedDialogType(null)}
            />
          ) : (
            <AssetAllowed
              busy={busy}
              allowed={allowed}
              formValues={formValues}
              assetCode={
                selectedCurrency?.displayCode || selectedCurrency?.code || ''
              }
            >
              <div className="relative flex whitespace-nowrap overflow-auto scrollbar-hide gap-2 px-4 pb-4">
                {ALLOWED_FILTER_ASSETS_BY_LIST.map((id, index) => (
                  <TabItemSwitcher
                    key={id}
                    active={id === assetFilter}
                    index={index}
                    onPress={busy ? undefined : handleAssetFilterChange(id)}
                    title={startCase(id)}
                    small
                    height={32}
                  />
                ))}
                <span
                  className={cx(
                    'absolute z-10 top-0 bottom-3 right-4',
                    searchCollapsed ? '' : 'left-4'
                  )}
                >
                  <SearchInput
                    autoFocus
                    disabled={busy}
                    value={assetSearch}
                    onChange={handleSearch}
                    collapsed={searchCollapsed}
                    onToggleCollapse={toggleSearchCollapsed}
                    maxLength={100}
                  />
                </span>
              </div>

              {!hasNoAssets && (
                <div className="py-4 px-6 border-b bg-grey-brightest">
                  <Row.LR>
                    <SortableHeaderTitle label={assetListHeader.name} />
                    <SortableHeaderTitle label={assetListHeader.value} />
                  </Row.LR>
                </div>
              )}
              <section className="flex flex-col rounded-b min-h-250 max-h-485 overflow-y-scroll">
                {hasNoAssets && (
                  <SearchEmpty title={assetSearch ? undefined : ''} />
                )}
                {groupedAssets.map((group, index) => {
                  if (!group) {
                    return null;
                  }
                  const hasData = group.data && group.data.length > 0;
                  return (
                    <div
                      key={JSON.stringify(group)}
                      className={
                        index + 1 === groupedAssets.length ? 'mb-6' : ''
                      }
                    >
                      {group.title && hasData && (
                        <span
                          className={cx(
                            'flex flex-row w-full justify-center items-center border-b px-2 bg-grey-brighter text-sm font-bold py-4'
                          )}
                        >
                          {group.title}
                        </span>
                      )}
                      {group.data.map((asset, _index, arr) => {
                        return (
                          <AssetItem
                            key={asset.currency.name}
                            asset={asset}
                            disabled={group.key === 'nonTradable'}
                            onSelected={onAssetSelected}
                            showPercentageChange={
                              (formValues.side === API.Side.Buy &&
                                !formValues.fromAsset) ||
                              (formValues.side === API.Side.Sell &&
                                !!formValues.fromAsset)
                            }
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </section>
            </AssetAllowed>
          )}
        </>
      </div>
    );
  }
);
